import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountCircleOutlined,
  Business,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Logout,
  Search,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import { APP_SHELL } from "../../../layouts/app/components/sidebar/MenuContent";
import logoMainDark123Zap from "../../../shared/assets/brand/logo-main-dark-123zap.png";
import { authRoutes } from "../config/authRoutes";
import { useAccountSelection } from "../hooks/useAccountSelection";

function getInitials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDocument(account) {
  const documentNumber = String(account.documentNumber || "").trim();

  if (!documentNumber) return "";

  const digits = documentNumber.replace(/\D/g, "");
  let formattedDocument = documentNumber;

  if (digits.length === 11) {
    formattedDocument = digits.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      "$1.$2.$3-$4"
    );
  } else if (digits.length === 14) {
    formattedDocument = digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }

  return formattedDocument;
}

function getAccountDescription(account) {
  const documentLabel = formatDocument(account);

  const accountDetails = [documentLabel].filter(Boolean);

  if (accountDetails.length) {
    return accountDetails.join(" · ");
  }

  return "Finalize os dados cadastrais para continuar.";
}

function getAccountDisplayName(account) {
  if (account.legalName || account.documentNumber) {
    return account.name;
  }

  return "Em configuração";
}

function getAccountInitialsSource(account) {
  if (account.legalName || account.documentNumber) {
    return account.name;
  }

  return account.name || "Conta";
}

function openDocumentation() {
  window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
}

function TopbarIconAction({
  children,
  label,
  onClick,
  controls,
  expanded,
  hasPopup,
  wide = false,
}) {
  return (
    <Tooltip title={label}>
      <ButtonBase
        aria-controls={controls}
        aria-expanded={expanded}
        aria-haspopup={hasPopup}
        aria-label={label}
        onClick={onClick}
        type="button"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          color: theme.palette.text.secondary,
          display: "flex",
          height: 36,
          justifyContent: "center",
          minWidth: wide ? 72 : 40,
          px: wide ? 1.5 : 0,
          "&:hover": {
            bgcolor: theme.palette.action.hover,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          },
          "&:focus-visible": {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            outline: "none",
          },
        })}
      >
        {children}
      </ButtonBase>
    </Tooltip>
  );
}

export default function SelectAccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("");
  const {
    accounts,
    accountsLabel,
    error,
    hasAccounts,
    loading,
    refreshing,
    reload,
    selectAccount,
    selectedAccountId: loadingAccountId,
  } = useAccountSelection();

  const filteredAccounts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) return accounts;

    return accounts.filter((account) => {
      const searchableText = [
        account.name,
        account.legalName,
        account.documentNumber,
        account.planName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [accounts, searchTerm]);

  const menuOpen = Boolean(menuAnchorEl);
  const isAccountSwitchMode =
    new URLSearchParams(location.search).get("modo") === "trocar" ||
    location.state?.mode === "switch-account";

  async function handleLogout() {
    setMenuAnchorEl(null);
    await logout();
    navigate(authRoutes.login, { replace: true });
  }

  if (!isAccountSwitchMode && !user?.accountSelectionRequired && user?.accountId) {
    return <Navigate to={authRoutes.dashboard} replace />;
  }

  if (String(user?.emailVerified).toLowerCase() !== "true") {
    return <Navigate to={authRoutes.verifyEmail} replace />;
  }

  const visibleCount = filteredAccounts.length;

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      })}
    >
      <Box
        component="header"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexShrink: 0,
          height: { xs: APP_SHELL.mobileHeaderHeight, md: APP_SHELL.topbarHeight },
          justifyContent: "space-between",
          px: { xs: 2, sm: 3, md: 5 },
        })}
      >
        <Box
          component="img"
          src={logoMainDark123Zap}
          alt="123zap"
          sx={{
            display: "block",
            height: { xs: 28, md: 32 },
            maxWidth: { xs: 116, md: 132 },
            objectFit: "contain",
            objectPosition: "left center",
            width: "auto",
          }}
        />

        <Stack alignItems="center" direction="row" spacing={1}>
          <TopbarIconAction
            label="Documentação"
            onClick={openDocumentation}
            wide
          >
            <Typography
              component="span"
              sx={{
                color: "currentColor",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: "18px",
              }}
            >
              Documentação
            </Typography>
          </TopbarIconAction>

          <TopbarIconAction
            controls={menuOpen ? "account-selection-menu" : undefined}
            expanded={menuOpen ? "true" : undefined}
            hasPopup="menu"
            label="Abrir opções do usuário"
            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
          >
            <AccountCircleOutlined sx={{ fontSize: 18 }} />
          </TopbarIconAction>
        </Stack>

        <Menu
          anchorEl={menuAnchorEl}
          id="account-selection-menu"
          onClose={() => setMenuAnchorEl(null)}
          open={menuOpen}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          PaperProps={{
            sx: {
              borderRadius: 1,
              minWidth: 180,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={handleLogout} sx={{ gap: 1.5, minHeight: 48 }}>
            <Logout fontSize="small" />
            Sair
          </MenuItem>
        </Menu>
      </Box>

      <Box
        sx={(theme) => ({
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 2.5, md: 2.75 },
        })}
      >
        <Typography
          component="p"
          sx={(theme) => ({
            color: theme.palette.primary.main,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.35,
            mb: 1.1,
          })}
        >
          Contas
        </Typography>

        <Typography
          component="h1"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: { xs: 28, sm: 32 },
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.08,
          })}
        >
          Selecione uma conta
        </Typography>

        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.35,
            mt: 0.45,
          })}
        >
          {loading ? "Carregando contas..." : accountsLabel}
        </Typography>

        <TextField
          disabled={loading || refreshing}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Pesquisar"
          size="small"
          value={searchTerm}
          sx={(theme) => ({
            mt: 2,
            width: "100%",
            "& .MuiInputBase-root": {
              bgcolor: theme.palette.background.paper,
              borderRadius: "6px",
              height: 43,
              pr: 0.75,
            },
            "& input": {
              fontSize: 14,
            },
          })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        component="main"
        sx={(theme) => ({
          bgcolor: theme.palette.surface.muted,
          flex: 1,
          px: { xs: 1.5, sm: 3, md: 5 },
          py: { xs: 2, md: 2.5 },
        })}
      >
        {error && (
          <Box
            sx={(theme) => ({
              bgcolor: theme.palette.feedback.error.surface,
              border: `1px solid ${theme.palette.feedback.error.border}`,
              borderRadius: 1,
              color: theme.palette.feedback.error.text,
              mb: 1.5,
              px: 2,
              py: 1.25,
            })}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}

        <Stack spacing={1}>
          {loading ? (
            <AccountsSkeleton />
          ) : hasAccounts && filteredAccounts.length ? (
            filteredAccounts.map((account) => (
              <AccountRow
                account={account}
                disabled={refreshing}
                key={account.id}
                loading={loadingAccountId === account.id}
                onAccess={selectAccount}
                onSelect={setSelectedCardId}
                selected={selectedCardId === account.id}
              />
            ))
          ) : (
            <EmptyAccounts
              description={
                hasAccounts
                  ? "Tente ajustar a pesquisa para localizar a conta desejada."
                  : "Não encontramos contas ativas vinculadas ao seu usuário."
              }
              onReload={reload}
              showReload={!hasAccounts}
              title={hasAccounts ? "Nenhuma conta encontrada" : "Nenhuma conta disponível"}
            />
          )}
        </Stack>
      </Box>

      <Box
        component="footer"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexShrink: 0,
          gap: 1.5,
          height: 58,
          justifyContent: "center",
        })}
      >
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 500,
            minWidth: 72,
            textAlign: "right",
          })}
        >
          {visibleCount ? `1-${visibleCount} de ${visibleCount}` : "0 de 0"}
        </Typography>
        <IconButton aria-label="Página anterior" disabled size="small">
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton aria-label="Próxima página" disabled size="small">
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );
}

function AccountRow({
  account,
  disabled,
  loading,
  onAccess,
  onSelect,
  selected,
}) {
  const accountDescription = getAccountDescription(account);
  const accountDisplayName = getAccountDisplayName(account);
  const accountInitialsSource = getAccountInitialsSource(account);
  const handleSelect = () => {
    if (!disabled) onSelect(account.id);
  };
  const handleAccess = (event) => {
    event.stopPropagation();
    if (!disabled) onAccess(account.id);
  };

  return (
    <Box
      component="div"
      onClick={handleSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleSelect();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={(theme) => ({
        alignItems: "center",
        bgcolor:
          loading || selected
            ? theme.palette.action.selected
            : theme.palette.background.paper,
        border: `1px solid ${
          selected ? theme.palette.primary.main : "transparent"
        }`,
        borderRadius: "8px",
        color: "inherit",
        cursor: disabled ? "default" : "pointer",
        display: "grid",
        gap: { xs: 1.5, md: 2 },
        gridTemplateColumns: {
          xs: "48px minmax(0, 1fr)",
          sm: "48px minmax(0, 1fr) auto",
        },
        minHeight: { xs: 112, sm: 72 },
        opacity: disabled && !loading ? 0.72 : 1,
        px: { xs: 1.5, md: 2 },
        py: 1,
        textAlign: "left",
        transition: "background-color 120ms ease, border-color 120ms ease, opacity 120ms ease",
        width: "100%",
        "&:focus-visible": {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          outline: "none",
        },
        "&:hover": {
          bgcolor: disabled ? theme.palette.action.selected : theme.palette.action.hover,
        },
      })}
    >
      <Avatar
        src={account.logoUrl || undefined}
        sx={(theme) => ({
          bgcolor: theme.palette.surface.inverse,
          color: theme.palette.background.paper,
          fontSize: 16,
          fontWeight: 800,
          height: 48,
          width: 48,
        })}
      >
        {account.logoUrl ? null : getInitials(accountInitialsSource)}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {accountDisplayName}
        </Typography>
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 14,
            lineHeight: 1.35,
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {accountDescription}
        </Typography>
      </Box>

      <Button
        disabled={disabled}
        onClick={handleAccess}
        size="small"
        sx={{
          borderRadius: 999,
          display: "inline-flex",
          fontSize: 13,
          height: 40,
          gridColumn: { xs: "1 / -1", sm: "auto" },
          justifySelf: "end",
          minWidth: { xs: 112, sm: 84 },
          px: 2,
        }}
        variant="contained"
      >
        {loading ? <CircularProgress color="inherit" size={16} /> : "Acessar"}
      </Button>
    </Box>
  );
}

function AccountsSkeleton() {
  return Array.from({ length: 2 }).map((_, index) => (
    <Stack
      alignItems="center"
      direction="row"
      key={index}
      spacing={2}
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        borderRadius: "8px",
        minHeight: 72,
        px: { xs: 1.5, md: 2 },
        py: 1,
      })}
    >
      <Skeleton height={48} variant="circular" width={48} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton height={22} width="26%" />
        <Skeleton height={18} width="38%" />
      </Box>
      <Skeleton
        height={40}
        sx={{ borderRadius: 999 }}
        width={84}
      />
    </Stack>
  ));
}

function EmptyAccounts({ description, onReload, showReload, title }) {
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        borderRadius: "8px",
        py: 8,
        textAlign: "center",
      })}
    >
      <Business color="disabled" sx={{ fontSize: 44 }} />
      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 14, maxWidth: 420 }}>
        {description}
      </Typography>
      {showReload && (
        <Button onClick={onReload} size="small" variant="outlined">
          Tentar novamente
        </Button>
      )}
    </Stack>
  );
}
